import { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, RefreshCw, Search } from 'lucide-react';
import { searchDynamicRules } from '@/api/dynamicRules';
import { describeSearchError } from './describeSearchError';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/dateFormat';
import { CopyButton } from '@/pages/OnPass/CopyButton';
import type { DynamicRuleMatch, DynamicRuleSourceResult } from '@/types/oscar';

/**
 * Dynamic rule search — IT Support types some text (a branch name, a department,
 * an old office) and sees every Entra dynamic group rule and Exchange dynamic
 * distribution group filter that contains it. Built for rename work, where a
 * stale name inside a rule silently changes who is in a group.
 *
 * ⚠ A source that could not be read is shown as a warning above the results and
 * the empty state never says "no rule contains it" unless BOTH sources were read:
 * "not found" from a directory we could not search is not an answer.
 *
 * Read-only. Server: GET /api/dynamic-rules/search (admin + helpdesk).
 */

const MIN = 2;
const MAX = 200;

const schema = z.object({
  q: z.string().trim().min(MIN, `Enter at least ${MIN} characters`).max(MAX, `Limited to ${MAX} characters`),
});
type FormData = z.infer<typeof schema>;

// Each search is its own query: a new id per submit, with `refresh` only for the
// Refresh button, so an ordinary search never forces a directory re-read.
interface Submitted { q: string; refresh: boolean; id: number }

const SOURCE_LABEL: Record<string, string> = { Entra: 'Entra ID', Exchange: 'Exchange Online' };

// Result filter. Client-side over the matches already returned: it never changes what is searched,
// so the directory status list above the results always shows BOTH directories either way.
type SourceFilter = 'All' | 'Entra' | 'Exchange';
const FILTERS: SourceFilter[] = ['All', 'Entra', 'Exchange'];
const FILTER_LABEL: Record<SourceFilter, string> = { All: 'All', Entra: 'Entra ID', Exchange: 'Exchange Online' };

/** Wrap each case-insensitive occurrence of `needle` in <mark>. */
function Highlight({ text, needle }: { text: string; needle: string }) {
  if (!needle) return <>{text}</>;
  const parts: { s: string; hit: boolean }[] = [];
  const lower = text.toLowerCase();
  const n = needle.toLowerCase();
  let i = 0;
  while (i < text.length) {
    const at = lower.indexOf(n, i);
    if (at === -1) { parts.push({ s: text.slice(i), hit: false }); break; }
    if (at > i) parts.push({ s: text.slice(i, at), hit: false });
    parts.push({ s: text.slice(at, at + n.length), hit: true });
    i = at + n.length;
  }
  return (
    <>
      {parts.map((p, k) =>
        p.hit
          ? <mark key={k} className="rounded bg-orange/25 px-0.5 text-navy">{p.s}</mark>
          : <Fragment key={k}>{p.s}</Fragment>)}
    </>
  );
}

function MatchCard({ match, needle }: { match: DynamicRuleMatch; needle: string }) {
  const { Item: it, MatchedIn } = match;
  const inRule = MatchedIn.includes('Rule');
  return (
    <li className="rounded border border-border bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-base font-bold text-navy"><Highlight text={it.Name} needle={needle} /></h3>
        <CopyButton value={it.Name} label="Copy" ariaLabel={`Copy group name ${it.Name}`} className="px-2 py-0.5 text-xs" />
        <span className="rounded-full bg-navy/10 px-2 py-0.5 text-xs font-semibold text-navy">{SOURCE_LABEL[it.Source] ?? it.Source}</span>
        <span className="text-xs text-darkGrey/70">{it.Kind}</span>
        {it.State && it.State !== 'On' && (
          <span className="rounded-full bg-orange/10 px-2 py-0.5 text-xs font-semibold text-orange">Rule processing {it.State.toLowerCase()}</span>
        )}
      </div>
      {it.Mail && <p className="mt-0.5 text-xs text-darkGrey/70">{it.Mail}</p>}

      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-darkGrey/60">{it.RuleLabel}</p>
      <pre className="mt-1 max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded bg-bg/60 p-2 font-mono text-xs text-darkGrey">
        {it.Rule ? (inRule ? <Highlight text={it.Rule} needle={needle} /> : it.Rule) : '—'}
      </pre>

      {it.Scope && (
        <p className="mt-2 text-xs text-darkGrey/80">
          <span className="font-semibold">Recipient container:</span>{' '}
          <span className="font-mono">{MatchedIn.includes('Scope') ? <Highlight text={it.Scope} needle={needle} /> : it.Scope}</span>
        </p>
      )}
    </li>
  );
}

function SourceStatus({ s }: { s: DynamicRuleSourceResult }) {
  const label = SOURCE_LABEL[s.Source] ?? s.Source;
  if (s.Status === 'ok') {
    return (
      <li className="text-xs text-darkGrey/80">
        <span className="font-semibold text-navy">{label}</span> — {s.GroupsChecked} dynamic{' '}
        {s.GroupsChecked === 1 ? 'group' : 'groups'} checked
        {s.RetrievedUtc && <> · read {formatDateTime(s.RetrievedUtc)}{s.FromCache && ' (cached)'}</>}
      </li>
    );
  }
  return (
    <li className="flex gap-2 rounded border border-red/30 bg-red/5 p-3 text-sm text-darkGrey" role="alert">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red" aria-hidden />
      <span>
        <span className="font-semibold text-red">{label} could not be searched.</span> {s.Error}{' '}
        <span className="text-darkGrey/80">Nothing from {label} is shown below, so a missing result does not mean it is clear.</span>
      </span>
    </li>
  );
}

export function DynamicRuleSearchPage() {
  const [submitted, setSubmitted] = useState<Submitted | null>(null);
  const [filter, setFilter] = useState<SourceFilter>('All');
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { q: '' },
  });

  const query = useQuery({
    queryKey: ['dynamic-rule-search', submitted?.id],
    queryFn: ({ signal }) => searchDynamicRules(submitted!.q, submitted!.refresh, signal),
    enabled: submitted !== null,
    staleTime: 0,
    gcTime: 60_000,
    retry: false,
  });

  // A NEW search resets the filter, so a leftover "Exchange" can never hide the new results. Re-reading
  // the same text keeps it.
  const onSubmit = handleSubmit(({ q }) => {
    setFilter('All');
    setSubmitted({ q, refresh: false, id: Date.now() });
  });
  const refresh = () => { if (submitted) setSubmitted({ q: submitted.q, refresh: true, id: Date.now() }); };

  const data = query.data;
  const matches = data?.Matches ?? [];
  const counts: Record<SourceFilter, number> = {
    All: matches.length,
    Entra: matches.filter((m) => m.Item.Source === 'Entra').length,
    Exchange: matches.filter((m) => m.Item.Source === 'Exchange').length,
  };
  const shown = filter === 'All' ? matches : matches.filter((m) => m.Item.Source === filter);
  const ruleHits = shown.filter((m) => m.MatchedIn.includes('Rule') || m.MatchedIn.includes('Scope'));
  const nameOnly = shown.filter((m) => !m.MatchedIn.includes('Rule') && !m.MatchedIn.includes('Scope'));
  const filteredSourceFailed = filter !== 'All' && (data?.Sources.some((s) => s.Source === filter && s.Status === 'error') ?? false);
  const errored = data?.Sources.filter((s) => s.Status === 'error') ?? [];
  const errorMessage = describeSearchError(query.error);

  return (
    <section>
      <header className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-navy">Dynamic rule search</h1>
        <p className="mt-1 text-sm text-darkGrey/80">
          Find every Entra dynamic group rule and Exchange dynamic distribution group filter that contains some text — for
          example a branch name before it is renamed. Read-only. To write a new rule from department codes and position
          titles, use the <Link to="/dynamic-rules/builder" className="font-semibold text-navy underline">Dynamic rule builder</Link>.
        </p>
      </header>

      <form onSubmit={onSubmit} className="mb-5 flex flex-wrap items-start gap-3" noValidate>
        <div className="min-w-[16rem] flex-1">
          <label htmlFor="dynamic-rule-q" className="block text-sm font-semibold text-navy">Text to find</label>
          <input
            id="dynamic-rule-q"
            type="search"
            autoComplete="off"
            placeholder="e.g. Brisbane"
            aria-invalid={errors.q ? 'true' : 'false'}
            aria-describedby={errors.q ? 'dynamic-rule-q-error' : undefined}
            className="mt-1 w-full rounded border border-border bg-white px-3 py-2 text-sm"
            {...register('q')}
          />
          {errors.q && <p id="dynamic-rule-q-error" className="mt-1 text-xs text-red">{errors.q.message}</p>}
        </div>
        <button
          type="submit"
          disabled={query.isFetching}
          className="mt-6 inline-flex items-center gap-2 rounded bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90 disabled:opacity-60"
        >
          <Search className="h-4 w-4" aria-hidden /> Search
        </button>
        {submitted && (
          <button
            type="button"
            onClick={refresh}
            disabled={query.isFetching}
            title="Skip the 5-minute cache and re-read Entra and Exchange"
            className="mt-6 inline-flex items-center gap-2 rounded border border-navy px-4 py-2 text-sm font-semibold text-navy hover:bg-navy/5 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? 'animate-spin' : ''}`} aria-hidden /> Re-read directories
          </button>
        )}
      </form>

      {query.isFetching && <p className="text-sm text-darkGrey/70" role="status">Searching Entra and Exchange… Exchange can take up to 30 seconds.</p>}
      {errorMessage && !query.isFetching && <p className="text-sm text-red" role="alert">{errorMessage}</p>}

      {data && !query.isFetching && (
        <>
          <ul className="mb-4 space-y-2" aria-label="Directories searched">
            {data.Sources.map((s) => <SourceStatus key={s.Source} s={s} />)}
          </ul>

          {data.Matches.length === 0 && (
            <p className="rounded border border-border bg-white p-4 text-sm text-darkGrey">
              {errored.length === 0
                ? <>No dynamic rule or filter contains <strong>{data.Query}</strong>.</>
                : <>No match for <strong>{data.Query}</strong> in the {data.Sources.length - errored.length === 1 ? 'directory' : 'directories'} that could be read.</>}
            </p>
          )}

          {matches.length > 0 && (
            <div role="group" aria-label="Filter results by directory" className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">Show</span>
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-navy',
                    filter === f ? 'border-navy bg-navy text-white' : 'border-border bg-white text-navy hover:bg-navy/5',
                  )}
                >
                  {FILTER_LABEL[f]} ({counts[f]})
                </button>
              ))}
            </div>
          )}

          {matches.length > 0 && shown.length === 0 && (
            <p className="rounded border border-border bg-white p-4 text-sm text-darkGrey">
              {filteredSourceFailed
                ? <>{FILTER_LABEL[filter]} could not be searched, so there is nothing to show for it.</>
                : <>No {FILTER_LABEL[filter]} results for <strong>{data!.Query}</strong>. Choose another filter to see the rest.</>}
            </p>
          )}

          {ruleHits.length > 0 && (
            <>
              <h2 className="mb-2 font-heading text-lg font-bold text-navy">Appears in a rule ({ruleHits.length})</h2>
              <ul className="mb-6 space-y-3">
                {ruleHits.map((m) => <MatchCard key={`${m.Item.Source}:${m.Item.Id}`} match={m} needle={data.Query} />)}
              </ul>
            </>
          )}

          {nameOnly.length > 0 && (
            <>
              <h2 className="mb-1 font-heading text-lg font-bold text-navy">Name only ({nameOnly.length})</h2>
              <p className="mb-2 text-xs text-darkGrey/70">The group&rsquo;s name contains the text but its rule does not.</p>
              <ul className="space-y-3">
                {nameOnly.map((m) => <MatchCard key={`${m.Item.Source}:${m.Item.Id}`} match={m} needle={data.Query} />)}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}
